/**
 * Réglages du widget ALTCHA, isolés du composant pour rester testables.
 *
 * Le widget est un custom element : ses options passent par des attributs HTML
 * (minuscules) ou par l'attribut `configuration` (JSON). On ne fabrique ici que
 * des valeurs — aucun DOM, aucun réseau.
 */

/**
 * Les deux expériences proposées au contributeur, dans le vocabulaire du widget.
 *
 * - `invisible` : rien à faire, le calcul démarre quand le visiteur entre dans
 *   le formulaire (`onfocus`) et se termine bien avant l'envoi ; le widget
 *   n'affiche qu'un état (`native`).
 * - `checkbox` : la case « Je ne suis pas un robot », que le visiteur coche
 *   lui-même — donc aucun démarrage automatique.
 */
export const CAPTCHA_MODES = {
    invisible: { auto: 'onfocus', type: 'native' },
    checkbox: { auto: 'off', type: 'checkbox' },
};

/**
 * Attributs `auto` / `type` correspondant à un mode. Un mode inconnu (faute de
 * frappe dans un bloc) retombe sur le mode invisible plutôt que de ne rien rendre.
 */
export function widgetAttributesForMode(mode) {
    return CAPTCHA_MODES[mode] ?? CAPTCHA_MODES.invisible;
}

/**
 * Valeur à écrire dans le store pour un état du widget.
 *
 * Seul l'état `verified` porte une preuve ; tout le reste (calcul en cours,
 * expiration, erreur) doit VIDER le chemin, sinon le bouton d'envoi resterait
 * déverrouillé avec un jeton périmé.
 */
export function payloadForState(state, event, element, name = 'altcha') {
    if (state !== 'verified') {
        return '';
    }

    const fromEvent = event?.detail?.payload;
    if (typeof fromEvent === 'string' && fromEvent !== '') {
        return fromEvent;
    }

    // Repli : le widget dépose aussi la preuve dans un input caché.
    const input = element?.querySelector?.(`input[name="${name}"]`);

    return typeof input?.value === 'string' ? input.value : '';
}

/**
 * Libellés annoncés aux technologies d'assistance, par état du widget.
 *
 * Le widget ne rend AUCUNE région live : son passage de « en cours » à
 * « Vérifié » est purement visuel (vérifié dans le DOM rendu). Un message
 * d'état doit être annoncé sans déplacer le focus — WCAG 2.2 4.1.3 (AA).
 */
export const STATUS_LABELS = {
    verifying: 'Vérification anti-robot en cours.',
    verified: 'Vérification anti-robot terminée.',
    expired: 'La vérification anti-robot a expiré.',
    error: 'La vérification anti-robot a échoué.',
};

/**
 * Message à annoncer pour un état, ou '' quand il n'y a rien à dire (état
 * initial : le visiteur n'a encore rien déclenché).
 */
export function statusMessage(state, labels = STATUS_LABELS) {
    return labels[state] ?? '';
}

/** Scripts déjà demandés, par URL : deux champs sur la même page ne le chargent qu'une fois. */
const loading = new Map();

/**
 * Charge un module servi par `public/` via une balise `<script type="module">`.
 *
 * Pourquoi pas un `import()` : un fichier de `public/` n'est PAS un module du
 * graphe Vite — le serveur de développement refuse de le transformer (500) et
 * renvoie « assets in public directory cannot be imported from JavaScript ».
 * La balise script, elle, est la façon prévue de les référencer, et le widget
 * s'enregistre de toute façon comme custom element global.
 */
export function loadModuleScript(url, doc = document) {
    if (loading.has(url)) {
        return loading.get(url);
    }

    const promise = new Promise((resolve, reject) => {
        const existing = doc.querySelector(`script[src="${url}"]`);
        if (existing?.dataset.loaded === 'true') {
            resolve();
            return;
        }

        const script = existing ?? doc.createElement('script');
        script.type = 'module';
        script.src = url;
        script.addEventListener('load', () => {
            script.dataset.loaded = 'true';
            resolve();
        });
        script.addEventListener('error', () => reject(new Error(`script non chargé : ${url}`)));
        if (!existing) {
            doc.head.appendChild(script);
        }
    });

    loading.set(url, promise);

    return promise;
}
